import { GoogleMapsContext, latLngEquals } from '@vis.gl/react-google-maps'
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from 'react'

function useCircle(props) {
  const {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    onRadiusChanged,
    onCenterChanged,
    radius,
    center,
    ...circleOptions
  } = props

  // Avoid re-triggering effects on callback changes
  const callbacks = useRef({})
  Object.assign(callbacks.current, {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    onRadiusChanged,
    onCenterChanged,
  })

  const circle = useRef(new google.maps.Circle()).current

  // Apply initial options
  circle.setOptions(circleOptions)

  // Update center
  useEffect(() => {
    if (!center) return
    const currentCenter = circle.getCenter()
    if (!latLngEquals(center, currentCenter)) {
      circle.setCenter(center)
    }
  }, [center])

  // Update radius
  useEffect(() => {
    if (radius == null) return
    if (radius !== circle.getRadius()) {
      circle.setRadius(radius)
    }
  }, [radius])

  // Attach to map
  const { map } = useContext(GoogleMapsContext) || {}

  useEffect(() => {
    if (!map) {
      if (map === undefined) {
        console.error('<Circle> must be used inside a <Map> component.')
      }
      return
    }

    circle.setMap(map)
    return () => {
      circle.setMap(null)
    }
  }, [map])

  // Attach events
  useEffect(() => {
    if (!circle) return

    const gme = google.maps.event

    const handlers = [
      ['click', 'onClick'],
      ['drag', 'onDrag'],
      ['dragstart', 'onDragStart'],
      ['dragend', 'onDragEnd'],
      ['mouseover', 'onMouseOver'],
      ['mouseout', 'onMouseOut'],
    ]

    const listeners = handlers.map(([eventName, callbackKey]) =>
      gme.addListener(circle, eventName, (e) => {
        const cb = callbacks.current[callbackKey]
        if (cb) cb(e)
      }),
    )

    const radiusChangedListener = gme.addListener(circle, 'radius_changed', () => {
      const newRadius = circle.getRadius()
      callbacks.current.onRadiusChanged?.(newRadius)
    })

    const centerChangedListener = gme.addListener(circle, 'center_changed', () => {
      const newCenter = circle.getCenter()
      callbacks.current.onCenterChanged?.(newCenter)
    })

    return () => {
      listeners.forEach((l) => l.remove())
      radiusChangedListener.remove()
      centerChangedListener.remove()
    }
  }, [circle])

  return circle
}

export const Circle = forwardRef((props, ref) => {
  const circle = useCircle(props)
  useImperativeHandle(ref, () => circle)
  return null
})
